'use client';
import React, { useEffect, useRef, useState } from 'react';
import { Bell, BellOff, BellRing } from 'lucide-react';
import { useFavorites } from '@/contexts/FavoritesContext';
import { useEvent } from '@/contexts/EventContext';

const NOTIF_STORAGE_KEY = 'mps_notif_scheduled';
const NOTIF_PERMISSION_KEY = 'mps_notif_asked';
const REMINDER_MINUTES = 15;

function parseActDateTime(dateStr: string, timeStr: string): Date | null {
  // dateStr: "14.05.2026", timeStr: "15:30"
  const parts = dateStr.split('.');
  if (parts.length !== 3) return null;
  const [day, month, year] = parts;
  const [hour, minute] = timeStr.split(':');
  const d = new Date(
    parseInt(year),
    parseInt(month) - 1,
    parseInt(day),
    parseInt(hour),
    parseInt(minute),
    0,
    0
  );
  return isNaN(d.getTime()) ? null : d;
}

function getScheduledIds(): Set<string> {
  try {
    const stored = localStorage.getItem(NOTIF_STORAGE_KEY);
    return stored ? new Set(JSON.parse(stored)) : new Set();
  } catch {
    return new Set();
  }
}

function saveScheduledIds(ids: Set<string>) {
  try {
    localStorage.setItem(NOTIF_STORAGE_KEY, JSON.stringify(Array.from(ids)));
  } catch {}
}

export default function FavoriteNotifications() {
  const { favorites } = useFavorites();
  const { selectedEvent } = useEvent();
  const [permission, setPermission] = useState<NotificationPermission>('default');
  const [supported, setSupported] = useState(false);
  const [showBanner, setShowBanner] = useState(false);
  const timeoutsRef = useRef<Map<string, ReturnType<typeof setTimeout>>>(new Map());

  useEffect(() => {
    if (typeof window !== 'undefined' && 'Notification' in window) {
      setSupported(true);
      setPermission(Notification.permission);
      // Show banner if not yet asked and there are favorites
      const asked = localStorage.getItem(NOTIF_PERMISSION_KEY);
      if (!asked && Notification.permission === 'default') {
        setShowBanner(true);
      }
    }
  }, []);

  // Schedule notifications whenever favorites or permission changes
  useEffect(() => {
    if (!supported || permission !== 'granted') return;

    const scheduled = getScheduledIds();
    const now = Date.now();

    // Clear old timeouts
    timeoutsRef.current.forEach((t) => clearTimeout(t));
    timeoutsRef.current.clear();

    const actFavorites = favorites.filter(f => f.itemType === 'act' && f.eventId === selectedEvent.id);

    actFavorites.forEach(fav => {
      // Find the act in the schedule to get its start time and date
      let actDate: string | null = null;
      let actStartTime: string | null = null;

      for (const day of selectedEvent.schedule) {
        const act = day.acts.find(a => a.id === fav.itemId);
        if (act) {
          actDate = day.date;
          actStartTime = act.startTime;
          break;
        }
      }

      if (!actDate || !actStartTime) return;

      const actStart = parseActDateTime(actDate, actStartTime);
      if (!actStart) return;

      const notifyAt = actStart.getTime() - REMINDER_MINUTES * 60 * 1000;
      const delay = notifyAt - now;

      const notifId = `${fav.itemId}-${actDate}`;

      if (delay <= 0) return; // Already past
      if (scheduled.has(notifId)) return; // Already scheduled in this session

      const timeout = setTimeout(() => {
        if (Notification.permission === 'granted') {
          try {
            new Notification(`🎵 ${fav.itemName} startet gleich!`, {
              body: `In ${REMINDER_MINUTES} Minuten auf ${selectedEvent.name} · ${actStartTime} Uhr`,
              icon: '/assets/images/app_logo.png',
              badge: '/assets/images/app_logo.png',
              tag: notifId,
            });
          } catch {}
        }
        // Remove from scheduled after firing
        const ids = getScheduledIds();
        ids.delete(notifId);
        saveScheduledIds(ids);
        timeoutsRef.current.delete(notifId);
      }, delay);

      timeoutsRef.current.set(notifId, timeout);
      scheduled.add(notifId);
    });

    saveScheduledIds(scheduled);

    return () => {
      timeoutsRef.current.forEach((t) => clearTimeout(t));
    };
  }, [favorites, permission, selectedEvent]);

  const requestPermission = async () => {
    try {
      localStorage.setItem(NOTIF_PERMISSION_KEY, '1');
      setShowBanner(false);
      const result = await Notification.requestPermission();
      setPermission(result);
    } catch {}
  };

  const dismissBanner = () => {
    localStorage.setItem(NOTIF_PERMISSION_KEY, '1');
    setShowBanner(false);
  };

  if (!supported) return null;

  // Inline toggle button (used in settings or nav)
  if (!showBanner) {
    return null;
  }

  // Permission request banner
  return (
    <div className="fixed bottom-20 left-0 right-0 z-50 px-4 pointer-events-none">
      <div className="max-w-md mx-auto pointer-events-auto">
        <div className="card-elevated rounded-2xl p-4 shadow-lg border border-primary/20 bg-background/95 backdrop-blur-sm">
          <div className="flex items-start gap-3">
            <div className="flex-shrink-0 w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center">
              <BellRing size={20} className="text-primary" />
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-semibold text-foreground">Benachrichtigungen aktivieren?</p>
              <p className="text-xs text-muted-foreground mt-0.5 leading-relaxed">
                Erhalte eine Erinnerung 15 Minuten vor deinen Favoriten-Acts.
              </p>
              <div className="flex gap-2 mt-3">
                <button
                  onClick={requestPermission}
                  className="flex-1 py-1.5 px-3 bg-primary text-primary-foreground text-xs font-semibold rounded-lg hover:bg-primary/90 transition-colors"
                >
                  Ja, aktivieren
                </button>
                <button
                  onClick={dismissBanner}
                  className="py-1.5 px-3 text-xs text-muted-foreground hover:text-foreground border border-border rounded-lg transition-colors"
                >
                  Nein danke
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

// Export a small toggle button for use in settings
export function NotificationToggle() {
  const [permission, setPermission] = useState<NotificationPermission>('default');
  const [supported, setSupported] = useState(false);

  useEffect(() => {
    if (typeof window !== 'undefined' && 'Notification' in window) {
      setSupported(true);
      setPermission(Notification.permission);
    }
  }, []);

  if (!supported) return null;

  const handleToggle = async () => {
    if (permission === 'granted') {
      // Can't revoke programmatically — inform user
      alert('Benachrichtigungen können nur in den Browser-Einstellungen deaktiviert werden.');
      return;
    }
    try {
      localStorage.setItem(NOTIF_PERMISSION_KEY, '1');
      const result = await Notification.requestPermission();
      setPermission(result);
    } catch {}
  };

  return (
    <button
      onClick={handleToggle}
      className="flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground transition-colors"
      title={permission === 'granted' ? 'Benachrichtigungen aktiv' : 'Benachrichtigungen aktivieren'}
    >
      {permission === 'granted' ? (
        <Bell size={16} className="text-primary" />
      ) : (
        <BellOff size={16} />
      )}
      <span className="text-xs">
        {permission === 'granted' ? 'Benachrichtigungen aktiv' : 'Benachrichtigungen aktivieren'}
      </span>
    </button>
  );
}
